import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import TriStateCheckbox from "@/components/triStateCheckbox.component";
import type {
	OrganizationMemberDTO,
	GruppeDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";

interface BulkGroupAssignmentProps {
	selectedMembers: OrganizationMemberDTO[];
	availableGroups: GruppeDTO[];
	userGroupAssignments: Record<string, string[]>;
	onToggleGroupForSelected: (groupId: string) => Promise<void>;
	onClose: () => void;
}
/*
 * BulkGroupAssignment component
 * @param selectedMembers - The selected members
 * @param availableGroups - The available groups
 * @param userGroupAssignments - The user group assignments
 * @param onToggleGroupForSelected - The function to toggle the group for the selected members
 * @param onClose - The function to close the component
 */
export const BulkGroupAssignment: React.FC<BulkGroupAssignmentProps> = ({
	selectedMembers,
	availableGroups,
	userGroupAssignments,
	onToggleGroupForSelected,
	onClose,
}) => {
	const selectedUserIds = selectedMembers.map((m) => m.id);

	const getCompletionColor = (isAll: boolean, isPartial: boolean) => {
		if (isAll) return "bg-green-100 text-green-900 border-green-200";
		if (isPartial) return "bg-yellow-100 text-yellow-900 border-yellow-200";
		return "bg-gray-100 text-gray-700 border-gray-200";
	};

	return (
		<div className="p-6 space-y-6">
			{/* Selected Users */}
			<div className="bg-gray-50 rounded-lg p-4">
				<div className="text-sm font-medium text-gray-900 mb-2">
					Ausgewählte Personen
				</div>
				<div className="text-sm text-gray-700">
					{selectedMembers
						.map((m) => (m.name?.trim() ? m.name : m.email))
						.join(", ")}
				</div>
			</div>

			{/* Groups Management */}
			<div>
				<div className="text-sm font-medium text-gray-900 mb-4">
					Gruppen verwalten
				</div>
				<div className="space-y-2 overflow-y-auto max-h-[50vh] pr-1">
					{availableGroups.map((group) => {
						const total = selectedUserIds.length;
						const assigned = selectedUserIds.reduce((acc, uid) => {
							const userGroups = new Set(userGroupAssignments[uid] ?? []);
							return acc + (userGroups.has(group.id) ? 1 : 0);
						}, 0);
						const isPartial = total > 0 && assigned > 0 && assigned < total;
						const isAll = total > 0 && assigned === total;

						return (
							<button
								key={group.id}
								type="button"
								className={`
									w-full border rounded-lg p-3 cursor-pointer transition-all group hover:shadow-sm text-left
									${
										isAll
											? "border-primary bg-primary/5 shadow-sm"
											: " hover:border-slate-300"
									}
								`}
								onClick={() => {
									void (async () => {
										await onToggleGroupForSelected(group.id);
									})();
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										void (async () => {
											await onToggleGroupForSelected(group.id);
										})();
									}
								}}
							>
								<div className="flex items-center justify-between mb-2">
									<div className="flex items-center gap-3">
										<TriStateCheckbox
											checked={
												isAll ? true : isPartial ? "indeterminate" : false
											}
											onChange={() => {
												void (async () => {
													await onToggleGroupForSelected(group.id);
												})();
											}}
											size="sm"
											aria-label={`Gruppe ${group.title} zuweisen`}
											stopPropagation
										/>
										<h3
											className={`font-medium text-sm truncate ${
												isAll ? "text-primary" : ""
											}`}
										>
											{group.title}
										</h3>
									</div>
									{selectedUserIds.length > 0 && (
										<Badge
											variant="outline"
											className={`gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-xl ${getCompletionColor(
												isAll,
												isPartial,
											)}`}
										>
											<Users className="h-3 w-3" />
											{assigned}/{total}
										</Badge>
									)}
								</div>

								{/* Status indicator */}
								<div className="flex items-center justify-between">
									<span className="text-xs text-muted-foreground">
										{isAll
											? "Vollständig zugewiesen"
											: isPartial
												? "Teilweise zugewiesen"
												: "Nicht zugewiesen"}
									</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			{/* Action Buttons */}
			<div className="sticky bottom-0 flex items-center gap-3">
				<Button variant="outline" onClick={onClose} className="flex-1">
					Schließen
				</Button>
			</div>
		</div>
	);
};

export default BulkGroupAssignment;
